/**
 * Golem fork (FT-1463): THE NIGHT ORDER BELONGS TO THE SCRIPT.
 *
 * The user's decision, verbatim: "Roles shouldn't have a night order value
 * attached to them. Scripts should have a list of their order."
 *
 * THE MODEL
 *   A ROLE says only WHETHER it wakes — first night yes/no, other nights
 *   yes/no (`wakesByDefault`). Its roles.json number (firstNight 43.33 …) is
 *   no longer an order anyone reads directly: it is a SEED, used in exactly
 *   two places — building a list for a script that has none, and choosing
 *   where a role lands when it is added to a script that has one.
 *
 *   A SCRIPT owns two ordered id lists, `firstNight: [id…]` and
 *   `otherNight: [id…]`, riding the edition object exactly the way
 *   `nightCopy` does (parsed off `_meta` by parseRoles, persisted with the
 *   edition stash, sent over the edition frame). The shape is the official
 *   script tool's own `_meta.firstNight` / `_meta.otherNight`, so a script
 *   exported by that tool loads its order as-is and ours reads back there.
 *   Membership IS waking: a script role absent from a night's list does not
 *   wake that night in this script (the workbench's Don't-wake box).
 *
 * WHAT A LIST HOLDS
 *   · script characters that wake that night — never Travellers (they left
 *     the script surface; a seated one is merged in by seed, see orderKey)
 *     and never Fabled (not script characters here);
 *   · the official tool's four pseudo-steps (dusk, minioninfo, demoninfo,
 *     dawn) WHEN an imported list carries them. They are kept in place so a
 *     round trip is lossless, and nothing here renders them or invents
 *     them: this app's night sheet has no group steps today (see nightInfo's
 *     GROUP WAKES note), so a list we build never contains one.
 *
 * NO LIST (an official edition, a script from another tool, a script saved
 * before this card) → the list is BUILT from the seeds, on read. FT-854's
 * per-script number overrides (`{ id, otherNight: 12.5 }` on a script entry)
 * are still overlaid onto the role by setCustomRoles, so an old reordered
 * script builds its list in the order its author dragged — that is the
 * migration, and the next edit materializes the list and drops the numbers.
 *
 * Pure functions, no Vue, no store — the store's getters and the workbench
 * call these.
 */

export const NIGHTS = ["firstNight", "otherNight"];

/** The official script tool's non-character steps. Preserved, never built. */
export const NIGHT_STEPS = ["dusk", "minioninfo", "demoninfo", "dawn"];

export const isStep = (id) => NIGHT_STEPS.includes(id);

/** Travellers never ride a script's night list (either spelling). */
export const isTraveller = (role) =>
  /^travell?er$/.test(String((role && role.team) || "").toLowerCase());

/** The same id wash setCustomRoles applies to every script entry. */
const cleanId = (id) =>
  String(id || "")
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Does this role wake on `night` unless a script says otherwise? A positive
 * seed, or a reminder for that night (a hand-authored character often has the
 * text and never had a number). Every official character agrees on both
 * tests; the reminder half is for custom ones.
 */
export function wakesByDefault(role, night) {
  if (!role) return false;
  return (Number(role[night]) || 0) > 0 || !!role[night + "Reminder"];
}

/** The seed a role sorts by; a waker with no number sorts last. */
export function seedOf(role, night) {
  const n = Number(role && role[night]);
  return n > 0 ? n : Infinity;
}

/**
 * A stored list, washed: an array of cleaned, de-duplicated ids — or null
 * when the value is not a list at all (the "build it" signal). An empty array
 * is a real list: a script where nobody wakes that night.
 */
export function sanitizeList(value) {
  if (!Array.isArray(value)) return null;
  const out = [];
  const seen = new Set();
  value.forEach((raw) => {
    const id = cleanId(typeof raw === "string" ? raw : raw && raw.id);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/** Build a night's list from seeds — the no-list path. `roles` are the
 *  script's characters; ties fall back to the name so the build is stable. */
export function buildList(roles, night) {
  return roles
    .filter((r) => r && r.id && !isTraveller(r) && wakesByDefault(r, night))
    .sort(
      (a, b) =>
        seedOf(a, night) - seedOf(b, night) ||
        String(a.name || a.id).localeCompare(String(b.name || b.id)),
    )
    .map((r) => r.id);
}

/**
 * The EFFECTIVE list for one night: the stored list with anything no longer
 * on the script dropped (steps kept), or the seed build when there is none.
 */
export function resolveList(stored, roles, night) {
  const list = sanitizeList(stored);
  if (!list) return buildList(roles, night);
  const onScript = new Set(
    roles.filter((r) => r && r.id && !isTraveller(r)).map((r) => r.id),
  );
  return list.filter((id) => isStep(id) || onScript.has(id));
}

/**
 * Where a role that is NOT in `list` would sit, by seed: the index of the
 * first listed character whose seed is higher. Steps carry no seed and are
 * stepped over. `lookup(id)` resolves a listed id to its role.
 */
function seedSlot(list, role, night, lookup) {
  const seed = seedOf(role, night);
  if (seed === Infinity) {
    // no seed: the end — but ahead of a trailing "dawn", which closes a night
    const dawn = list.lastIndexOf("dawn");
    return dawn >= 0 && dawn === list.length - 1 ? dawn : list.length;
  }
  for (let i = 0; i < list.length; i++) {
    const id = list[i];
    if (isStep(id)) continue;
    const other = lookup(id);
    if (other && seedOf(other, night) > seed) return i;
  }
  const dawn = list.lastIndexOf("dawn");
  return dawn >= 0 && dawn === list.length - 1 ? dawn : list.length;
}

/**
 * THE SORT KEY every ordered night surface uses. A listed role sorts at its
 * list index. A role the list cannot name — a seated Traveller, a believed
 * character off the script, a Fabled — is merged in by seed, half a step
 * before the listed role it would precede, so it never collides with one.
 */
export function orderKey(list, role, night, lookup) {
  const at = list.indexOf(role && role.id);
  if (at >= 0) return at;
  return seedSlot(list, role, night, lookup) - 0.5;
}

/**
 * Does this role wake on `night` in this script?
 *   listed                      → yes
 *   a script character, unlisted → no (deliberately asleep in this script)
 *   anything else               → its own default (Travellers, Fabled, a
 *                                  believed character from another script)
 * `scriptIds` holds the script's non-Traveller character ids.
 */
export function wakesIn(list, scriptIds, role, night) {
  if (!role || !role.id) return false;
  if (list.includes(role.id)) return true;
  if (scriptIds.has(role.id)) return false;
  return wakesByDefault(role, night);
}

/** Insert `role` into `list` at its seed position (a copy is returned). */
export function insertBySeed(list, role, night, lookup) {
  if (!role || !role.id || list.includes(role.id)) return list.slice();
  const out = list.slice();
  out.splice(seedSlot(list, role, night, lookup), 0, role.id);
  return out;
}

/**
 * A drag in the workbench's night tab: move `id` next to `targetId`
 * (`after` = below it). Moving a script sleeper into the list is the same
 * call — it simply was not there before. Everything else, steps included,
 * keeps its place. Returns null when the target is not in the list.
 */
export function moveInList(list, id, targetId, after) {
  const out = list.filter((x) => x !== id);
  let at = out.indexOf(targetId);
  if (at < 0) return null;
  if (after) at += 1;
  out.splice(at, 0, id);
  return out;
}

/**
 * THE EDITOR'S RECONCILE — what the lists become after one script edit.
 *
 *   before      { firstNight, otherNight } — the effective lists BEFORE
 *   beforeRoles Map id → role, the script BEFORE
 *   afterRoles  the script's roles AFTER (array)
 *   replace     optional { from, to } — the forge saved `to` over `from`
 *               (the same id for an in-place edit; a new id for a fork)
 *
 * Rules, per night:
 *   · a role that left the script leaves the list;
 *   · a role that joined inserts at its seed position if it wakes by
 *     default (a custom role with no seed goes to the end);
 *   · a REPLACED role keeps its slot (under its new id) unless the edit
 *     switched its wake OFF; one that was not listed joins only if the edit
 *     switched its wake ON — so an unrelated edit never wakes a character
 *     the author put in Don't-wake.
 */
export function reconcileLists(before, beforeRoles, afterRoles, replace) {
  const afterById = new Map(
    afterRoles.filter((r) => r && r.id).map((r) => [r.id, r]),
  );
  const lookup = (id) => afterById.get(id) || beforeRoles.get(id);
  const out = {};
  NIGHTS.forEach((night) => {
    let list = (before[night] || []).filter(
      (id) => isStep(id) || afterById.has(id) || (replace && id === replace.from),
    );
    if (replace && replace.to) {
      const oldRole = beforeRoles.get(replace.from);
      const newRole = afterById.get(replace.to.id) || replace.to;
      const was = list.indexOf(replace.from);
      const oldWakes = wakesByDefault(oldRole, night);
      const newWakes = wakesByDefault(newRole, night) && !isTraveller(newRole);
      if (was >= 0) {
        if (newWakes || !oldWakes) list[was] = newRole.id;
        else list.splice(was, 1);
        list = list.filter((id, i) => id !== newRole.id || i === list.indexOf(id));
      } else if (newWakes && !oldWakes) {
        list = insertBySeed(list, newRole, night, lookup);
      }
    }
    afterRoles.forEach((role) => {
      if (!role || !role.id || isTraveller(role)) return;
      if (beforeRoles.has(role.id)) return;
      if (replace && replace.to && role.id === replace.to.id) return;
      if (wakesByDefault(role, night)) {
        list = insertBySeed(list, role, night, lookup);
      }
    });
    out[night] = list;
  });
  return out;
}

/** 1-based position among the list's CHARACTERS (steps not counted), 0 when
 *  absent — the integer a custom role's own `firstNight` / `otherNight`
 *  carries on export, so another tool reading only those numbers still
 *  orders it right. Never a fraction. */
export function exportPosition(list, id) {
  const roles = list.filter((x) => !isStep(x));
  return roles.indexOf(id) + 1;
}
